const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const HANDLING_TASK_STATUSES = new Set([
  "UNASSIGNED",
  "ASSIGNED",
  "PICKED",
  "HANDLING",
  "IN_PROGRESS",
  "OPEN_AND_ASSIGNED",
  "CONTINUE",
  "START-REVIEW",
  "CONTINUE-REVIEW",
  "CLOSED",
  "DONE",
]);

const NEW_EMAIL_EVENT_TYPES = new Set([
  "NEW_EMAIL",
  "NEW_MAIL",
  "EMAIL_RECEIVED",
  "MAIL_RECEIVED",
  "NEW_MESSAGE",
  "MESSAGE_RECEIVED",
  "EMAIL_MESSAGE_CREATED",
]);

type OutlookTagEventType = "OUTLOOK_TAG_SYNCED" | "OUTLOOK_TAG_CLEARED";

export type OutlookTagSseUpdate = {
  conversationId: string;
  messageId: string;
  eventType: OutlookTagEventType;
  status: string | null;
  categories: string[];
};

type ConversationWithMessages = {
  id?: unknown;
  msConversationId?: unknown;
  messages?: unknown;
};

type ConversationWithTaskStatus = {
  id?: unknown;
  msConversationId?: unknown;
  taskStatus?: unknown;
  status?: unknown;
  assignee?: unknown;
};

export type ConversationStatusSseUpdate = {
  conversationId: string;
  taskStatus: string;
  assignee?: string | null;
};

const normalizeIdentifier = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getSignalParameters = (payload: unknown): Record<string, unknown> | null => {
  if (!isRecord(payload) || payload.type !== "SIGNAL" || !isRecord(payload.parameters)) {
    return null;
  }

  return payload.parameters;
};

const normalizeTaskStatus = (value: unknown): string => {
  const status = normalizeIdentifier(value).toUpperCase();
  return HANDLING_TASK_STATUSES.has(status) ? status : "";
};

const getTaskStatusFromRecord = (record: Record<string, unknown>): string => {
  const explicitStatus = [
    record.new_task_status,
    record.newTaskStatus,
    record.new_status,
    record.newStatus,
    record.new_value,
    record.newValue,
    record.task_status,
    record.taskStatus,
    record.conversation_status,
    record.conversationStatus,
    record.status,
  ]
    .map(normalizeTaskStatus)
    .find(Boolean);
  if (explicitStatus) return explicitStatus;

  const eventType = normalizeIdentifier(record.event_type || record.event)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");

  if (
    eventType === "UNASSIGNED" ||
    eventType === "RELEASE" ||
    eventType === "RELEASE_CONVERSATION" ||
    eventType === "CONVERSATION_RELEASE" ||
    eventType.endsWith("_RELEASED") ||
    eventType.includes("CANCEL_HANDLING") ||
    eventType.includes("HANDLING_CANCEL")
  ) {
    return "UNASSIGNED";
  }

  if (
    eventType === "ASSIGNED" ||
    eventType === "PICKED" ||
    eventType === "ASSIGN_TO_ME" ||
    eventType === "ASSIGNED_TO_ME" ||
    eventType.endsWith("_ASSIGNED") ||
    eventType.includes("START_HANDLING") ||
    eventType.includes("HANDLING_START")
  ) {
    return "ASSIGNED";
  }

  return "";
};

const getAssigneeFromRecord = (
  record: Record<string, unknown>
): { found: boolean; value: string | null } => {
  const keys = [
    "assignee",
    "assignee_email",
    "assigneeEmail",
    "assigned_to",
    "assignedTo",
    "assigned_user_email",
    "assignedUserEmail",
    "user_email",
    "userEmail",
    "operator_email",
    "operatorEmail",
  ];

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue;

    const value = record[key];
    if (value === null) return { found: true, value: null };

    const assignee = normalizeIdentifier(value);
    return { found: true, value: assignee || null };
  }

  return { found: false, value: null };
};

export function getConversationStatusUpdateFromSSE(
  payload: unknown
): ConversationStatusSseUpdate | null {
  const signal = getSignalParameters(payload);
  if (!signal) return null;

  const conversationId = normalizeIdentifier(
    signal.conversation_id || signal.conversationId || signal.conversationID
  );
  if (!conversationId) return null;

  const eventRecords = Array.isArray(signal.events) ? signal.events.filter(isRecord).reverse() : [];
  const statusSource = [...eventRecords, signal].find((record) => getTaskStatusFromRecord(record));
  if (!statusSource) return null;

  const taskStatus = getTaskStatusFromRecord(statusSource);
  const sourceAssignee = getAssigneeFromRecord(statusSource);
  const signalAssignee = getAssigneeFromRecord(signal);
  const assignee = sourceAssignee.found ? sourceAssignee : signalAssignee;

  return {
    conversationId,
    taskStatus,
    ...(taskStatus === "UNASSIGNED"
      ? { assignee: null }
      : assignee.found
      ? { assignee: assignee.value }
      : {}),
  };
}

const isNewEmailSignal = (payload: unknown): boolean => {
  const signal = getSignalParameters(payload);
  if (!signal) return false;

  const eventTypes = [
    signal.event,
    ...(Array.isArray(signal.events)
      ? signal.events.filter(isRecord).map((event) => event.event_type || event.event)
      : []),
  ];

  return eventTypes.some((eventType) => {
    const normalizedEventType = normalizeIdentifier(eventType)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_");
    return NEW_EMAIL_EVENT_TYPES.has(normalizedEventType);
  });
};

export function applyConversationStatusUpdateToConversations<T extends ConversationWithTaskStatus>(
  conversations: T[],
  update: ConversationStatusSseUpdate
): T[] {
  let didUpdate = false;
  const updatedConversations = conversations.map((conversation) => {
    const isTargetConversation = [conversation.id, conversation.msConversationId].some(
      (id) => normalizeIdentifier(id) === update.conversationId
    );
    if (!isTargetConversation) return conversation;

    const currentTaskStatus = normalizeTaskStatus(conversation.taskStatus || conversation.status);
    const currentAssignee = normalizeIdentifier(conversation.assignee) || null;
    const nextAssignee = update.assignee === undefined ? currentAssignee : update.assignee;
    if (currentTaskStatus === update.taskStatus && currentAssignee === nextAssignee) {
      return conversation;
    }

    didUpdate = true;
    return {
      ...conversation,
      taskStatus: update.taskStatus,
      status: update.taskStatus,
      ...(update.assignee === undefined ? {} : { assignee: update.assignee }),
    } as T;
  });

  return didUpdate ? updatedConversations : conversations;
}

export function getOutlookTagUpdateFromSSE(payload: unknown): OutlookTagSseUpdate | null {
  if (!isRecord(payload)) return null;

  const signal = isRecord(payload.parameters) ? payload.parameters : payload;
  const conversationId = normalizeIdentifier(signal.conversation_id);
  const messageId = normalizeIdentifier(signal.message_id);
  if (!conversationId || !messageId || !Array.isArray(signal.events)) return null;

  const tagEvent = [...signal.events]
    .reverse()
    .find(
      (event): event is Record<string, unknown> =>
        isRecord(event) &&
        (event.event_type === "OUTLOOK_TAG_SYNCED" || event.event_type === "OUTLOOK_TAG_CLEARED")
    );
  if (!tagEvent) return null;

  const status = normalizeIdentifier(tagEvent.status) || null;
  const categories = Array.isArray(tagEvent.categories)
    ? tagEvent.categories.map(normalizeIdentifier).filter(Boolean)
    : status
    ? [status]
    : [];

  return {
    conversationId,
    messageId,
    eventType: tagEvent.event_type as OutlookTagEventType,
    status,
    categories,
  };
}

export function applyOutlookTagUpdateToConversations<T extends ConversationWithMessages>(
  conversations: T[],
  update: OutlookTagSseUpdate
): T[] {
  let didUpdate = false;
  const updatedConversations = conversations.map((conversation) => {
    const isTargetConversation = [conversation.id, conversation.msConversationId].some(
      (id) => normalizeIdentifier(id) === update.conversationId
    );
    if (!isTargetConversation || !Array.isArray(conversation.messages)) return conversation;

    let didUpdateMessage = false;
    const updatedMessages = conversation.messages.map((message) => {
      if (!isRecord(message)) return message;

      const isTargetMessage = [message.id, message.messageId].some(
        (id) => normalizeIdentifier(id) === update.messageId
      );
      if (!isTargetMessage) return message;

      didUpdateMessage = true;
      return {
        ...message,
        status: update.status,
        categories: update.categories,
        category: undefined,
      };
    });

    if (!didUpdateMessage) return conversation;
    didUpdate = true;
    return { ...conversation, messages: updatedMessages } as T;
  });

  return didUpdate ? updatedConversations : conversations;
}

const normalizeMailbox = (mailbox: string): string => mailbox.trim().toLowerCase();

const getPayloadMailbox = (payload: unknown): string => {
  const signal = isRecord(payload) && isRecord(payload.parameters) ? payload.parameters : payload;
  if (!isRecord(signal)) return "";

  const mailbox = signal.mailbox;
  return typeof mailbox === "string" ? mailbox.trim() : "";
};

export function shouldRefreshEmailBenchFromSSE(
  payload: unknown,
  selectedMailboxAddress: string
): boolean {
  const outlookTagUpdate = getOutlookTagUpdateFromSSE(payload);
  if (!getSignalParameters(payload) && !outlookTagUpdate) return false;
  // Patching loaded messages cannot add/remove conversations from the server's
  // category-filtered results. Tag changes must also invalidate that list.
  if (
    !outlookTagUpdate &&
    !isNewEmailSignal(payload) &&
    getConversationStatusUpdateFromSSE(payload)
  )
    return false;

  const payloadMailbox = getPayloadMailbox(payload);
  if (!payloadMailbox) return true;

  const selectedMailbox = selectedMailboxAddress.trim();
  if (!selectedMailbox) return false;

  return normalizeMailbox(payloadMailbox) === normalizeMailbox(selectedMailbox);
}
